import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { GoogleLogo, ArrowRight } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { Disclaimer } from "@/components/Disclaimer";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const IMG =
  "https://images.pexels.com/photos/30175799/pexels-photo-30175799.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1000";

export default function Register() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.has_password) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      // If a lead session exists (email captured during assessment), set a password instead.
      if (user && !user.has_password) {
        await api.post("/auth/set-password", { password });
        await api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => {});
        navigate("/dashboard");
        return;
      }
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const isLead = user && !user.has_password;

  return (
    <div className="grid min-h-screen bg-slate-950 lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:order-1 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <Logo />
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="mt-10 font-heading text-3xl font-light tracking-tight text-slate-50">
              {isLead ? "Save your profile" : "Create your account"}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {isLead ? "Set a password to keep your living profile and unlock your AI Coach." : "Begin your living profile. It only gets more accurate."}
            </p>

            {!isLead && (
              <>
                <button
                  onClick={handleGoogle}
                  className="mt-8 flex w-full items-center justify-center gap-3 rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-medium text-slate-200 transition-all hover:border-slate-500"
                  data-testid="google-register-btn"
                >
                  <GoogleLogo size={18} weight="bold" /> Continue with Google
                </button>
                <div className="my-6 flex items-center gap-3 text-xs text-slate-600">
                  <span className="h-px flex-1 bg-slate-800" /> or <span className="h-px flex-1 bg-slate-800" />
                </div>
              </>
            )}

            <form onSubmit={submit} className="space-y-4">
              {!isLead && (
                <>
                  <input
                    type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                    className="w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-slate-200 placeholder-slate-600 outline-none transition-all focus:border-teal-500 focus:ring-2 focus:ring-teal-500/40"
                    data-testid="register-name-input"
                  />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" required
                    className="w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-slate-200 placeholder-slate-600 outline-none transition-all focus:border-teal-500 focus:ring-2 focus:ring-teal-500/40"
                    data-testid="register-email-input"
                  />
                </>
              )}
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Choose a password (min 6 chars)" required minLength={6}
                className="w-full rounded-lg border border-slate-800 bg-slate-950 px-4 py-3 text-slate-200 placeholder-slate-600 outline-none transition-all focus:border-teal-500 focus:ring-2 focus:ring-teal-500/40"
                data-testid="register-password-input"
              />
              {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
              <button
                type="submit" disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-slate-950 transition-all hover:bg-teal-400 disabled:opacity-50"
                data-testid="register-submit-btn"
              >
                {loading ? "Creating…" : isLead ? "Save Profile" : "Create account"} {!loading && <ArrowRight size={16} weight="bold" />}
              </button>
            </form>

            <p className="mt-6 text-sm text-slate-500">
              Already have an account?{" "}
              <Link to="/login" className="font-medium text-teal-400 hover:text-teal-300">Log in</Link>
            </p>
            <div className="mt-8">
              <Disclaimer compact />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="relative hidden lg:block lg:order-2 grain-overlay">
        <img src={IMG} alt="" className="h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-950/10" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="font-heading text-3xl font-light leading-snug text-slate-100">
            One living profile. <span className="text-amber-400">Infinite depth</span> over time.
          </p>
        </div>
      </div>
    </div>
  );
}
